
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import Toast from '../../helpers/Toast';
import { GetFileDTO } from '../../models/File/GetFileDTO';
import { GetPaginatedFileListDTO } from '../../models/File/GetPaginatedFileListDTO';
import { AssistanceService } from '../../services/AssistanceService';
import { FileTypeService } from '../../services/FileTypeService';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import ModalComponent from '../ModalComponent';
import { SelectItemsProps } from '../Select2Component';
import SelectComponent from '../SelectComponent';
import TableComponent from '../TableComponent';
import ChooseFileButton from './ChooseFileButton';
import ImagePopup from './ImagePopUp';
import JSZip from "jszip"
import saveAs from 'file-saver';
import useValidationSchema, { FieldConfig } from '../Validations';

export type FilesListProps = {
    assistanceId: number;
    reloadExecutions: boolean;
    mode: "workshop" | "backoffice";
}

export function FilesList({ assistanceId, reloadExecutions, mode }: FilesListProps) {

    const intl = useIntl();
    const zip = new JSZip();
    const [files, setFiles] = useState<GetFileDTO[]>([]);
    const assistanceService = new AssistanceService();
    const fileTypeService = new FileTypeService();
    const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
    const [file, setFile] = useState<File | null>(null);
    const [resetFile, setResetFile] = useState<number>(0);
    const [goToPage, setGoToPage] = useState<number>(1);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [removeFile, setRemovedFile] = useState<GetFileDTO>();
    const [notes, setNotes] = useState<string>("");
    const [reload, setReload] = useState<boolean>(false);

    const [fileTypeSelected, setFiletypeSelected] = useState<string>("")
    const [listTypeFiles, setListTypeFiles] = useState<SelectItemsProps[]>([]);

    const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(true);



    const onFileClick = (path: string,) => {
        if (path.startsWith("data:image") || path.startsWith("data:video")) {
            setFullScreenImage(path);
        } else {
            let newWindow = window.open("");
            newWindow!.document.write(`<iframe width='100%' height='100%' src=${path}></iframe>`);
        }
    }
    const onFileChange = (file: File | null) => {
        setFile(file);
    }

    const fields: FieldConfig[] = [
        ['fileTypeSelected', 'string', 'Screens.Assistances.Files.Insert.Validation.FileTypeRequired'],
        ['notes', 'string', 'Screens.Assistances.Files.Insert.Validation.NotesRequired']
    ];

    const schemaClientValidation = useValidationSchema(fields);

    const insertFile = async () => {
        if (typeof file === "undefined" || file == null) {
            Toast.Show("error", intl.formatMessage({ id: "Common.Files.FilesPopup.FileMustBeSelected" }));
            return;
        }

        const insertedFile = {
            fileTypeSelected: fileTypeSelected,
            notes: notes
        }

        const { error } = schemaClientValidation.validate(insertedFile);

        if (error) {
            Toast.Show("error", error.details[0].message);
            setIsButtonLoading(false);
            return;
        }

        setIsLoadingTable(true);
        setIsButtonLoading(true);

        var response = await assistanceService.InsertFile(file, assistanceId, fileTypeSelected, notes);

        if (response.success === false) {
            setIsButtonLoading(false);
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }

        setFile(null);
        setResetFile(resetFile + 1);
        setIsLoadingTable(false);
        setIsButtonLoading(false);
        Toast.Show("success", intl.formatMessage({ id: "Common.Files.FilesPopup.InsertSuccess" }));
        setReload(!reload);

    }

    const fetchData = async (currentPage: number, pageSize: number) => {
        setIsLoadingTable(true);

        var data: GetPaginatedFileListDTO = {
            currentPage: currentPage,
            pageSize: pageSize,
            assistanceId: assistanceId,
        }

        var response = await assistanceService.GetFiles(data);

        if (response.count <= 0) {
            setIsDownloadDisabled(true);

        } else {
            setIsDownloadDisabled(false);
        }

        if (response.success === false) {
            setFiles([]);
            setGoToPage(0);
            setIsLoadingTable(false);
            setIsDownloadDisabled(true);
            Toast.Show("error", response.message);
            return;
        }

        setFiles(response.items);
        setIsLoadingTable(false);
        setTotalPages(response.totalPages);
        setPageSize(pageSize);
        setCurrentPage(currentPage);
        setGoToPage(0)

        if (response.totalPages > 0 && currentPage > response.totalPages) {
            setGoToPage(1);
        }
    };

    const getTypeFile = async () => {
        var response = await fileTypeService.GetAll();
        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        var items: SelectItemsProps[] = [];
        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.id.toString(),
                label: element.name
            }
            items.push(item);
        });

        setListTypeFiles(items);
    }

    const downloadFiles = async () => {

        setIsButtonLoading(true);
        var response = await assistanceService.GetAllFilesFromAssistance(assistanceId);

        if (response.success === false) {
            setIsButtonLoading(false);
            setIsDownloadDisabled(true);
            Toast.Show("error", response.message);
            return;
        }

        response.obj.forEach((file, index) => {
            zip.file(files[index].name, convertBase64ToFile(file.source, files[index].name))
        })
        zip.generateAsync({ type: "blob" }).then(content => {
            saveAs(content, "files.zip");
        });

        setIsButtonLoading(false);
    }

    const DownloadSingleFile = async (fileId: number) => {

        try {
            setIsButtonLoading(true);
       
        var response = await assistanceService.DownloadSingleFile(fileId);

        if (response.success === false) {
            setIsButtonLoading(false);
            setIsDownloadDisabled(true);
            Toast.Show("error", response.message);
            return;
        }
        
        const fileContent = convertBase64ToFile(response?.obj?.source, response?.obj?.originalName ?? "");
        const blob = new Blob([fileContent], { type: response?.obj?.fileType });

        // Trigger download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = response?.obj?.originalName ?? "";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        Toast.Show("success", "O ficheiro foi transferido com sucesso!");
    }catch (error) {
        Toast.Show("error", "Ocoreu um erro ao transferir o ficheiro.");
    } finally {
        setIsButtonLoading(false);
    }
}












    const convertBase64ToFile = (base64String: any, fileName: string) => {
        let arr = base64String.split(',');
        let mime = arr[0].match(/:(.*?);/)[1];
        let bstr = atob(arr[1]);
        let n = bstr.length;
        let uint8Array = new Uint8Array(n);
        while (n--) {
            uint8Array[n] = bstr.charCodeAt(n);
        }
        let file = new File([uint8Array], fileName, { type: mime });
        return file;
    }


    const Remove = async (idFile: number) => {
        setIsLoadingTable(true);
        var response = await assistanceService.RemoveFile(assistanceId, idFile);
        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return
        }
        setIsLoadingTable(false);
        Toast.Show("success", intl.formatMessage({ id: "Common.Files.FilesPopup.RemoveSuccess" }));
        setReload(!reload);

    }



    useEffect(() => {
        getTypeFile();
    }, [])

    useEffect(() => {
        fetchData(currentPage, pageSize)
    }, [reload])

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Assistances.Files.Type" />,
                accessor: "fileType",
                width: 30,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Files.Desc" />,
                accessor: "notes",
                width: 35,
            },
            {
                Header: "Opções",
                Cell: (props: any) => {
                    return (
                        <>
                            <div style={{ display: "flex", justifyContent: "space-around" }}>

                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.See" })}
                                        disabled={isLoadingTable}
                                        onClick={() => {
                                            onFileClick(props.row.original.source);
                                        }} />
                                </div>
                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.Remove" })}
                                        disabled={isLoadingTable}
                                        onClick={() => {
                                            setIsOpen(true);
                                            setRemovedFile(props.cell.row.original)
                                        }} />
                                </div>

                                <div style={{ display: "inline-flex", height: "30px" }}>
                                    <ButtonComponent
                                        text={intl.formatMessage({ id: "FilesList.Download" })}
                                        onClick={() => DownloadSingleFile(props.row.original.fileId)}

                                        loading={isButtonLoading}
                                         disabled={false}
                                    />
                                </div>



                            </div>
                        </>
                    );
                },
                width: 35,
            }
        ],
        [],
    );

    return (
        <>
            <Row style={{ margin: "1rem 0px" }} >
                <ChooseFileButton reset={resetFile} onFileChange={onFileChange} />
                <SelectComponent xl={3} lg={3} md={3} sm={6} xs={12}
                    style={{ width: "100%" }}
                    label={"Tipo de Ficheiro"}
                    selectedItemValue={fileTypeSelected}
                    showLoadingSkeleton={isButtonLoading}
                    items={listTypeFiles}
                    onChange={(t: string) => setFiletypeSelected(t)}
                />

                <InputComponent xl={3} lg={3} md={3} sm={6} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    value={notes}
                    label={"Descrição"}
                    readOnly={false}
                    showLoadingSkeleton={isButtonLoading}
                    onChange={(t: string) => setNotes(t)}
                />
                <Col xl={3} lg={3} sm={3} xs={12}>
                    <ButtonComponent
                        style={{ width: '100%', marginTop: '30px' }}
                        onClick={() => insertFile()}
                        text={intl.formatMessage({ id: "FilesList.Insert" })}
                        loading={isButtonLoading}
                    />
                </Col>


                <h5 style={{ marginTop: "10px" }}><FormattedMessage id={"Screens.Assistances.Files.Table.Title"}></FormattedMessage></h5>
                <ModalComponent IsOpen={isOpen} text={`Tem a certeza que quer remover o ficheiro '${removeFile?.originalName}' da assistência com o id ${assistanceId}`} OnClickRight={() => setIsOpen(false)} OnClickLeft={() => { setIsOpen(false); Remove(removeFile?.fileId ?? 0) }} title={"Remover ficheiro da assistência"} buttonRight={"Não"} buttonLeft={"Sim"} />
                <TableComponent
                    columns={columns}
                    data={files}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={totalPages}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoadingTable}
                    fetchData={fetchData}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Row>
            <Row style={{ display: "flex", justifyContent: "start", alignItems: "center", margin: "auto" }}>
                <Col xl={4} lg={4} md={4} sm={4} xs={12}>
                    <ButtonComponent
                        style={{ width: '100%', marginBottom: "20px" }}
                        onClick={() => downloadFiles()}
                        text={intl.formatMessage({ id: "FilesList.Download" })}
                        loading={isButtonLoading}
                        disabled={isDownloadDisabled}
                    />
                </Col>
            </Row>

            <ImagePopup path={fullScreenImage} onClose={() => setFullScreenImage(null)} />
        </>
    )
}
